/**
 * PDF optimization planning.
 *
 * Everything here is pure so it can be unit tested: which images a level is
 * allowed to touch, how far to scale them, and whether the result is worth
 * swapping in. The decoding and re-encoding lives in `optimizePdf.web.ts`.
 *
 * The guiding rule is that an optimizer must never make a document worse. Any
 * image we are not certain about is left byte-for-byte as the author wrote it,
 * and a re-encoded image is only used when it is meaningfully smaller.
 */

export type OptimizeLevel = 'lossless' | 'high' | 'balanced' | 'small'

/** What an optimization run produced. */
export type OptimizeResult = {
  /** Bytes to upload — always a valid PDF, either optimized or the input. */
  bytes: Uint8Array
  /** True when `bytes` is the optimized output, not the untouched input. */
  applied: boolean
  originalSize: number
  optimizedSize: number
  /** Images whose bytes were rewritten */
  imagesRewritten: number
  /** Images left exactly as they were, for any reason */
  imagesSkipped: number
}

export type OptimizeOptions = {
  /** Page count of the input, re-checked against the output before it is used */
  expectedPageCount?: number
  signal?: AbortSignal
  /** Fraction (0..1) of the images considered so far */
  onProgress?: (fraction: number) => void
}

/**
 * Default level. `high` holds roughly 200dpi on A4, which is above what a
 * reader can resolve on screen at 100% — the point of the feature is a smaller
 * upload, not a visible change.
 */
export const DEFAULT_OPTIMIZE_LEVEL: OptimizeLevel = 'high'

export const OPTIMIZE_LEVELS: readonly OptimizeLevel[] = [
  'lossless',
  'high',
  'balanced',
  'small',
]

/**
 * `maxEdge` caps an image's long edge in pixels. A4 at 300dpi is ~3500px, so
 * `high` lands near 200dpi, `balanced` near 150dpi and `small` near 100dpi.
 * `jpegQuality` is the encoder quality used when re-encoding photos and scans.
 *
 * `lossless` keeps every page image byte-identical — it only repacks the file.
 */
export const OPTIMIZE_PRESETS: Record<
  OptimizeLevel,
  {maxEdge: number; jpegQuality: number}
> = {
  lossless: {maxEdge: 0, jpegQuality: 0},
  high: {maxEdge: 2200, jpegQuality: 0.85},
  balanced: {maxEdge: 1600, jpegQuality: 0.78},
  small: {maxEdge: 1100, jpegQuality: 0.7},
}

export function levelTouchesImages(level: OptimizeLevel): boolean {
  return OPTIMIZE_PRESETS[level].maxEdge > 0
}

/** Target dimensions for an image under a level. Never upscales. */
export function scaleForLevel(
  width: number,
  height: number,
  level: OptimizeLevel,
): {width: number; height: number; scale: number} {
  const {maxEdge} = OPTIMIZE_PRESETS[level]
  if (!maxEdge || width <= 0 || height <= 0) {
    return {width, height, scale: 1}
  }
  const scale = Math.min(1, maxEdge / Math.max(width, height))
  if (scale >= 1) return {width, height, scale: 1}
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  }
}

/** The parts of an image XObject's dictionary that decide what we can do. */
export type PdfImageInfo = {
  /** Filter names in order, eg. `['FlateDecode']`; empty means uncompressed */
  filters: string[]
  bitsPerComponent: number
  /** `DeviceRGB` / `DeviceGray`, or anything else we do not understand */
  colorSpace: string
  width: number
  height: number
  /** `/ImageMask` — a stencil drawn with the current fill colour */
  imageMask: boolean
  /** `/Mask` — a colour key or an explicit stencil sharing this image's pixels */
  hasMask: boolean
  /** `/SMask` — a soft (alpha) mask painted with this image, at its size */
  hasSoftMask: boolean
  hasDecodeParms: boolean
}

export type ImagePlan =
  | {action: 'skip'; reason: string}
  | {action: 'jpeg'; width: number; height: number; quality: number}

const SKIP_REASONS = {
  level: 'level keeps images untouched',
  mask: 'stencil mask',
  masked: 'has a colour key mask',
  decodeParms: 'has decode parameters',
  filterChain: 'unsupported filter chain',
  colorSpace: 'unsupported colour space',
  bits: 'unsupported bit depth',
  alreadySmall: 'already at the target size',
} as const

/**
 * Decides what may happen to one image. Anything we are not certain about is
 * skipped, which leaves those bytes exactly as the author wrote them — an
 * image we do not understand must never come out looking different.
 */
export function planImage(info: PdfImageInfo, level: OptimizeLevel): ImagePlan {
  if (!levelTouchesImages(level)) {
    return {action: 'skip', reason: SKIP_REASONS.level}
  }
  if (info.imageMask) return {action: 'skip', reason: SKIP_REASONS.mask}
  // A colour key mask names exact sample values of this very image, so any
  // re-encoding would change which pixels it selects.
  if (info.hasMask) return {action: 'skip', reason: SKIP_REASONS.masked}
  if (info.hasDecodeParms) {
    return {action: 'skip', reason: SKIP_REASONS.decodeParms}
  }

  const isJpeg = info.filters.length === 1 && info.filters[0] === 'DCTDecode'
  const isFlate = info.filters.length === 1 && info.filters[0] === 'FlateDecode'
  const isRaw = info.filters.length === 0
  if (!isJpeg && !isFlate && !isRaw) {
    return {action: 'skip', reason: SKIP_REASONS.filterChain}
  }
  // A JPEG is already 8-bit; only the sample-based encodings have a depth we
  // would have to unpack ourselves.
  if (!isJpeg && info.bitsPerComponent !== 8) {
    return {action: 'skip', reason: SKIP_REASONS.bits}
  }
  if (info.colorSpace !== 'DeviceRGB' && info.colorSpace !== 'DeviceGray') {
    return {action: 'skip', reason: SKIP_REASONS.colorSpace}
  }

  const {width, height, scale} = scaleForLevel(info.width, info.height, level)
  // A JPEG that is not being resized is already at its final quality; a second
  // lossy pass would only degrade it.
  if (isJpeg && scale >= 1) {
    return {action: 'skip', reason: SKIP_REASONS.alreadySmall}
  }

  // A soft mask is painted with its image and has to have the same dimensions,
  // so an image that carries one is only ever re-encoded at its own size. That
  // still buys most of the win — scans are usually stored losslessly, so a
  // first JPEG pass shrinks them several-fold.
  if (info.hasSoftMask) {
    return {
      action: 'jpeg',
      width: info.width,
      height: info.height,
      quality: OPTIMIZE_PRESETS[level].jpegQuality,
    }
  }

  return {
    action: 'jpeg',
    width,
    height,
    quality: OPTIMIZE_PRESETS[level].jpegQuality,
  }
}

/**
 * How much smaller a re-encoded image has to be to replace the original. A
 * losslessly-encoded source has nothing to lose from a first lossy pass, but an
 * image that was already JPEG is being re-encoded, so it has to buy a real win
 * to justify the extra generation.
 */
export function minSavingFor(info: PdfImageInfo): number {
  return info.filters[0] === 'DCTDecode' ? 0.15 : 0.05
}

/**
 * Whether a re-encoded result is worth swapping in. Re-encoding a small image
 * can grow it, and a bigger file for no visual gain is a bad trade.
 */
export function shouldReplaceImage(
  originalBytes: number,
  encodedBytes: number,
  {minSaving = 0.05}: {minSaving?: number} = {},
): boolean {
  if (originalBytes <= 0 || encodedBytes <= 0) return false
  return encodedBytes < originalBytes * (1 - minSaving)
}

/** The bytes to actually use: never a bigger file than the one we started with. */
export function chooseBytes(
  original: Uint8Array,
  candidate: Uint8Array | null,
): {bytes: Uint8Array; savedBytes: number; used: boolean} {
  if (!candidate || candidate.length >= original.length) {
    return {bytes: original, savedBytes: 0, used: false}
  }
  return {
    bytes: candidate,
    savedBytes: original.length - candidate.length,
    used: true,
  }
}
